import React from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { BodyTextSmall, ProgressBar, IconButton } from '@modernhealth/vital-components';

import { createRenderSwitch } from 'src/shared/components/RenderSwitch';
import { CreatePathwayQuestionnaireAvailability } from 'src/modules/pathways/components/CreatePathwayQuestionnaireAvailability';
import { CreatePathwayQuestionnaireTimezone } from 'src/modules/pathways/components/CreatePathwayQuestionnaireTimezone';
import { usePathwaysNavigation } from 'src/modules/pathways/hooks/navigation';

export enum QuestionnaireType {
    AVAILABILITY = 'availability',
    TIMEZONE = 'timezone',
}

const QuestionnaireTypeToTitleKey: Record<QuestionnaireType, string> = {
    [QuestionnaireType.AVAILABILITY]: 'pathways:onboarding.questionnaire.availability.title',
    [QuestionnaireType.TIMEZONE]: 'pathways:onboarding.questionnaire.timezone.title',
};

const Switch = createRenderSwitch<
    QuestionnaireType,
    { pathwayUuid: string; nextQuestionnaireType: QuestionnaireType | undefined }
>();

const QUESTIONNAIRE_ORDER = [QuestionnaireType.AVAILABILITY, QuestionnaireType.TIMEZONE];
export const DEFAULT_QUESTIONNAIRE_TYPE = QUESTIONNAIRE_ORDER[0];

export const PathwayCreateQuestionnairePage = () => {
    const { navigate } = usePathwaysNavigation();
    const { uuid, questionnaireType = DEFAULT_QUESTIONNAIRE_TYPE } = useParams<{
        uuid: string;
        questionnaireType?: QuestionnaireType | undefined;
    }>();
    const { t } = useTranslation();

    const currentIndex = QUESTIONNAIRE_ORDER.findIndex(q => q === questionnaireType);
    const nextIndex = currentIndex + 1;
    const nextQuestionnaireType: QuestionnaireType | undefined = QUESTIONNAIRE_ORDER[nextIndex];
    const progress = nextIndex / QUESTIONNAIRE_ORDER.length;

    return (
        <Container>
            <IconButton
                title={t('common.close')}
                onClick={() => navigate.pathway({ uuid })}
                icon="x"
                size="medium"
                variant="secondary"
                css={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                }}
            />

            <BodyTextSmall centered marginBottom={20}>
                {t(QuestionnaireTypeToTitleKey[questionnaireType])}
            </BodyTextSmall>
            <ProgressBar progress={progress} css={{ marginBottom: 50 }} />
            <Switch
                expression={questionnaireType}
                data={{ pathwayUuid: uuid, nextQuestionnaireType }}
            >
                <Switch.Case
                    expression={QuestionnaireType.AVAILABILITY}
                    component={CreatePathwayQuestionnaireAvailability}
                />
                <Switch.Case
                    expression={QuestionnaireType.TIMEZONE}
                    component={CreatePathwayQuestionnaireTimezone}
                />
            </Switch>
        </Container>
    );
};

const Container = styled.div({
    width: '100%',
    maxWidth: 500,
    margin: '0 auto',
    marginTop: 100,
    padding: 16,
});
